// Project list ordering — most recently modified first.
//
// The projects DynamoDB Query returns items in SK order, and SK is a random
// request UUID assigned at creation — i.e. effectively arbitrary order. The
// items themselves carry the timestamps we need:
//   - createdAt: always present (written by projects_post.vtl on create)
//   - updatedAt: only present once the project was modified (project_put.vtl)
// Both are ISO 8601 UTC strings ("2026-07-22T06:15:30.123Z") — the VTL
// templates build exactly the same format as the client's
// new Date().toISOString() — so Date.parse gives a total order.

export interface ProjectLike {
  updatedAt?: string;
  createdAt?: string;
  name?: string;
}

// Effective last-modification time in epoch ms: updatedAt wins, else createdAt.
// Unparseable / missing timestamps sort as 0 (i.e. to the end of a desc list).
export function projectModifiedAtMs(project: ProjectLike): number {
  const raw = project.updatedAt || project.createdAt || '';
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

// Returns a NEW array sorted by last modification, newest first.
// Ties (or missing timestamps) fall back to name ascending, case-insensitive,
// so the order is stable and deterministic.
export function sortProjectsByLastModified<T extends ProjectLike>(projects: T[]): T[] {
  return [...projects].sort((a, b) => {
    const diff = projectModifiedAtMs(b) - projectModifiedAtMs(a);
    if (diff !== 0) {
      return diff;
    }
    return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
  });
}
